import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Landmark } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { atoAPI, errMsg, money } from '../lib/atoAPI';
import { MockBanner } from '../components/ato/AtoBits';
import { AtoCorrespondence } from '../components/ato/AtoCorrespondence';
import { AtoAccounts, AtoAsReports, AtoClientDetails, AtoLodgments } from '../components/ato/AtoData';
import { AtoRules, AtoSettings, AtoSync } from '../components/ato/AtoAdmin';

const CARDS = [
  ['needs_review', 'Needs review', 'correspondence'], ['unprocessed', 'Unprocessed', 'correspondence'], ['awaiting_delivery', 'Awaiting delivery', 'correspondence'],
  ['client_ato_balances', 'Client ATO balances', 'accounts', true], ['outstanding_lodgments', 'Outstanding lodgments', 'lodgments'],
  ['overdue_flagged', 'Overdue (ATO data)', 'lodgments'], ['bas_ias_obligations', 'BAS/IAS obligations', 'lodgments'], ['payment_followups', 'Payment follow-ups', 'correspondence'],
  ['failed_syncs', 'Failed syncs', 'sync'], ['clients_needing_authorisation_review', 'Unmatched / authorisation review', 'correspondence'],
];

const Dashboard = ({ data, go }) => (!data ? null : (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="ato-dashboard-cards">
      {CARDS.map(([k, label, tab, isMoney]) => (
        <button key={k} onClick={() => go(tab)} data-testid={`ato-card-${k}`}
          className="rounded-lg border bg-card p-4 text-left transition-colors hover:border-primary/50 hover:bg-muted/40">
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="mt-1 text-2xl font-semibold tabular-nums">{isMoney ? money(data.cards[k]) : data.cards[k]}</div>
        </button>))}
    </div>
    <p className="text-xs text-muted-foreground" data-testid="ato-last-sync">
      Last successful sync: {Object.values(data.last_sync).sort().pop()?.slice(0, 16).replace('T', ' ') || 'never'} · environment {data.environment}</p>
  </div>
));

export default function AtoCentre() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'dashboard';
  const [access, setAccess] = useState(null);
  const [dash, setDash] = useState(null);
  const [nonce, setNonce] = useState(0);
  const go = (t) => setParams({ tab: t });
  const refresh = useCallback(() => { setNonce((n) => n + 1); atoAPI.dashboard().then(setDash).catch(() => {}); }, []);
  useEffect(() => { atoAPI.access().then(setAccess).catch((e) => toast.error(errMsg(e))); refresh(); }, [refresh]);

  if (access && !access.can_view) return <div className="p-8 text-sm text-muted-foreground" data-testid="ato-no-access">You don't have access to the ATO Centre.</div>;
  const w = !!access?.can_write;
  const a = !!access?.is_admin;
  return (
    <div className="space-y-5 p-6" data-testid="ato-centre-page">
      <div className="flex items-center gap-3"><Landmark className="h-6 w-6 text-primary" strokeWidth={1.5} />
        <div><h1 className="text-2xl font-semibold tracking-tight">ATO Centre</h1>
          <p className="text-sm text-muted-foreground">ATO correspondence, accounts and lodgments for your clients.</p></div></div>
      <MockBanner text={access?.banner} />
      <Tabs value={tab} onValueChange={go}>
        <TabsList className="flex h-auto flex-wrap justify-start">
          {['dashboard', 'correspondence', 'accounts', 'lodgments', 'as-reports', 'details', 'rules', 'sync', 'settings'].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`ato-tab-${t}`} className="capitalize">{t === 'as-reports' ? 'AS Reports' : t === 'details' ? 'Client details' : t}</TabsTrigger>))}
        </TabsList>
        <div className="mt-4" key={nonce}>
          <TabsContent value="dashboard"><Dashboard data={dash} go={go} /></TabsContent>
          <TabsContent value="correspondence"><AtoCorrespondence canWrite={w} /></TabsContent>
          <TabsContent value="accounts"><AtoAccounts /></TabsContent>
          <TabsContent value="lodgments"><AtoLodgments canWrite={w} /></TabsContent>
          <TabsContent value="as-reports"><AtoAsReports canWrite={w} /></TabsContent>
          <TabsContent value="details"><AtoClientDetails canWrite={w} /></TabsContent>
          <TabsContent value="rules"><AtoRules isAdmin={a} canWrite={w} /></TabsContent>
          <TabsContent value="sync"><AtoSync canWrite={w} onSynced={refresh} /></TabsContent>
          <TabsContent value="settings"><AtoSettings isAdmin={a} onChanged={refresh} /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
